import { useGenericMutation } from '../react-query';
import { apiClient } from '../../apiHelper';
import { AxiosResponse } from 'axios';
import { dicomQueryKeys } from './dicom-query-keys';

// Interfaces
export interface WorklistQueryRequest extends Record<string, unknown> {
  server_id: number;
  scheduled_station_ae_title?: string;
  scheduled_procedure_step_start_date?: string;
  scheduled_procedure_step_start_time?: string;
  modality?: string;
  scheduled_performing_physician_name?: string;
  patient_id?: string;
  patient_name?: string;
  patient_birth_date?: string;
  patient_sex?: string;
  accession_number?: string;
  requesting_physician?: string;
  study_instance_uid?: string;
  local_datetime?: string;
}

export interface MWLData {
  PatientID?: string;
  PatientName?: string;
  PatientBirthDate?: string;
  PatientSex?: string;
  AccessionNumber?: string;
  RequestingPhysician?: string;
  StudyInstanceUID?: string;
  RequestedProcedureDescription?: string;
  RequestedProcedureID?: string;
  RequestedProcedurePriority?: string;
  SpecificCharacterSet?: string;
  OtherPatientIDs?: string;
  MedicalAlerts?: string;
  Allergies?: string;
  PatientTelephoneNumbers?: string;
  PatientSpeciesDescription?: string;
  ScheduledProcedureStepSequence?: Array<{
    Modality?: string;
    ScheduledStationAETitle?: string;
    ScheduledProcedureStepStartDate?: string;
    ScheduledProcedureStepStartTime?: string;
    ScheduledPerformingPhysicianName?: string;
    ScheduledProcedureStepDescription?: string;
    ScheduledProcedureStepID?: string;
    ScheduledStationName?: string;
    ScheduledProcedureStepLocation?: string;
    PreMedication?: string;
    CommentsOnTheScheduledProcedureStep?: string;
  }>;
}

export interface PregnancyResolution {
  id: number;
  patient_id: string;
  pregnancy_status: string | null;
  dating_method: string | null;
  last_menstrual_period: string | null;
  estimated_due_date: string | null;
  ultrasound_date: string | null;
  closed_at?: string | null;
  outcome_date?: string | null;
  preg_bpd: number | null;
  preg_hc: number | null;
  preg_ac: number | null;
  preg_fl: number | null;
  preg_crl: number | null;
  grossesse_classee_a_risque?: boolean | null;
  risque_anemie?: boolean;
  risque_hta?: boolean;
  risque_diabete?: boolean;
  risque_cardiopathie?: boolean;
  risque_metrorragie?: boolean;
  risque_infection?: boolean;
  risque_preeclampsie?: boolean;
  risque_eclampsie?: boolean;
  risque_autre_details?: string | null;
  ga_bpd_1984: number | null;
  ga_hc_1984: number | null;
  ga_ac_1984: number | null;
  ga_fl_1984: number | null;
  ga_nichd_2017: number | null;
  ga_aua: number | null;
  efw_hadlock_ii: number | null;
  efw_hadlock_iii: number | null;
  efw_hadlock_iv: number | null;
  efw_hadlock_v: number | null;
  efw_intergrowth_21st: number | null;
  efw_who_2017: number | null;
  is_medically_active: boolean | null;
}

export interface MWLResolution {
  patient_exists: boolean;
  patient_uuid: string | null;
  patient_id_internal: string | null;
  appointment_exists: boolean;
  appointment_id: string | null;
  active_pregnancy_exists: boolean;
  pregnancy: PregnancyResolution | null;
  study_exists: boolean;
  study_instance_uid: string | null;
  facility_id?: string | null;
  facility_name?: string | null;
  division_id?: string | null;
  division_name?: string | null;
  external_location_code?: string | null;
  facility_matched?: boolean;
  facility_created?: boolean;
  belongs_to_selected_facility?: boolean | null;
}

export interface EnrichedWorklistItem {
  mwl: MWLData;
  resolution: MWLResolution;
}

export interface WorklistItem extends MWLData {}

export interface WorklistQueryResponse {
  query_level: string;
  total_results: number;
  results: EnrichedWorklistItem[];
  server_info: {
    server_id: string;
    ip: string;
    port: string;
    ae_title: string;
    tls_enabled?: string;
  };
  resolution_stats: {
    patients_found: number;
    pregnancies_found: number;
    studies_found: number;
    appointments_found: number;
  };
  query_time_ms: number;
  enrichment_time_ms: number;
  total_execution_time_ms: number;
}

export interface DicomQueryResult {
  success: boolean;
  message: string;
  query_level: string;
  total_results: number;
  results: WorklistItem[];
  server_info: {
    server_id: string;
    ip: string;
    port: string;
    ae_title: string;
  };
  execution_time_ms: number;
}

/**
 * Query DICOM Modality Worklist
 */
export function useQueryWorklist(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = false
) {
  const queryWorklistFn = async (data: WorklistQueryRequest) => {
    const response = await apiClient.post(`/dicom/query/worklist`, data);
    return response;
  };

  return useGenericMutation(
    queryWorklistFn,
    [...dicomQueryKeys.all],
    'Error while querying worklist',
    'Worklist query completed successfully',
    undefined,
    onSuccess,
    onError,
    notification
  );
}


export function useQueryRemoteWorklist(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = false
) {
  const queryFn = async (data: { aeId: number } & Record<string, unknown>) => {
    const { aeId, ...filters } = data;
    return apiClient.post(`/mwl/remote/${aeId}/worklist`, filters);
  };

  return useGenericMutation(
    queryFn,
    [...dicomQueryKeys.all],
    'Error while querying worklist',
    'Worklist query completed successfully',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

/**
 * Query DICOM Patients
 */
export function useQueryPatients(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = false
) {
  const queryPatientsFn = async (data: any) => {
    const response = await apiClient.post(`/dicom/query/patients`, data);
    return response;
  };

  return useGenericMutation(
    queryPatientsFn,
    dicomQueryKeys.all,
    'Error while querying patients',
    'Patient query completed successfully',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

/**
 * Query DICOM Studies
 */
export function useQueryStudies(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = false
) {
  const queryStudiesFn = async (data: any) => {
    const response = await apiClient.post(`/dicom/query/studies`, data);
    return response;
  };

  return useGenericMutation(
    queryStudiesFn,
    dicomQueryKeys.all,
    'Error while querying studies',
    'Study query completed successfully',
    undefined,
    onSuccess,
    onError,
    notification
  );
}
